<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <!-- Cabecalho no padrao das telas da area do credor/procurador -->
    <header class="hd-header">
      <span class="hd-eyebrow">
        <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
        {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
      </span>
      <h1 class="hd-title">Habilitações <span>e divergências</span></h1>
      <p class="hd-subtitle">Pedidos enviados à Administração Judicial, com a situação de cada um e os documentos.</p>
    </header>

    <!-- Texto configurado pelo AJ (orientacoes da area) -->
    <div v-if="!user ? dataset.contentUserAnonymous : dataset.contentUserLogged" class="hd-intro"
      v-html="!user ? dataset.contentUserAnonymous : dataset.contentUserLogged"></div>

    <login-required-notice v-if="!user" />

    <div v-else>
      <div class="hd-field">
        <procurator-creditor-selector v-model="selectedCreditorId" allow-pending></procurator-creditor-selector>
      </div>

      <div class="hd-list-head">
        <h2>Pedidos</h2>
        <span v-if="data.length">{{ data.length }} {{ data.length === 1 ? "pedido" : "pedidos" }}</span>
      </div>

      <div v-if="loading && !data.length" class="hd-skeleton">
        <v-skeleton-loader v-for="n in 2" :key="n" type="list-item-avatar-two-line" class="mb-2" />
      </div>

      <div v-else-if="!data.length" class="hd-empty">
        <div class="hd-empty__icon"><v-icon size="34" color="darkSecondary">mdi-file-document-multiple-outline</v-icon></div>
        <div class="hd-empty__title">
          {{ isProcurator && !selectedCreditorId ? "Selecione o credor representado" : "Nenhum pedido enviado" }}
        </div>
        <div class="hd-empty__text">
          {{ isProcurator && !selectedCreditorId ? "Os pedidos aparecem depois que você escolhe o credor acima." : "As habilitações e divergências enviadas aparecem aqui, com a situação de cada uma." }}
        </div>
      </div>

      <article v-for="(item, i) in data" :key="i" class="hd-card">
        <div class="hd-card__head">
          <div class="hd-card__icon"><v-icon color="darkSecondary">mdi-file-document-edit-outline</v-icon></div>
          <div class="hd-card__main">
            <div class="hd-card__title">Pedido #{{ i + 1 }} · {{ item.client.name }}</div>
            <div class="hd-muted">Solicitado em {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm') }}</div>
          </div>
          <span class="hd-pill" :class="`hd-pill--${statusTone(displayStatus(item))}`">
            <span class="hd-pill__dot"></span>{{ getTextByStatus(displayStatus(item)) }}
          </span>
          <v-btn v-if="item.status == 0" id="btnEdit" depressed color="primary" class="hd-btn" @click="editItem(item)">
            <v-icon left small>mdi-pencil-outline</v-icon> Editar
          </v-btn>
        </div>

        <!-- Solicitacao de novos documentos e por pedido: cada hab/div mostra o seu aviso -->
        <div v-if="item.status == 4 || (item.status == 5 && item.requested_out_of_date)" class="hd-notice"
          :class="item.requested_out_of_date ? 'hd-notice--muted' : 'hd-notice--warn'">
          <v-icon :color="item.requested_out_of_date ? '#6b6475' : '#a16207'">mdi-file-alert-outline</v-icon>
          <div class="hd-notice__text">
            <template v-if="!item.requested_out_of_date">
              <strong>Há uma solicitação de novos documentos para este pedido.</strong>
              O prazo para envio vai até {{ item.requested_deadline_label }}.
            </template>
            <template v-else>
              <strong>Houve uma solicitação de novos documentos para este pedido.</strong>
              O prazo para envio terminou {{ item.requested_deadline_label }}.
            </template>
          </div>
          <v-btn v-if="!item.requested_out_of_date" depressed color="primary" class="hd-btn" @click="loadRequestedDocuments(item)">
            <v-icon left small>mdi-upload</v-icon> Enviar documentos
          </v-btn>
        </div>

        <!-- Itens do pedido (origem e valor), cada um abre com detalhes e documentos -->
        <v-expansion-panels flat multiple class="hd-items">
          <v-expansion-panel v-for="(eventItem, eventIndex) in item.items" :key="eventIndex">
            <v-expansion-panel-header>
              <div class="hd-item-head">
                <span class="hd-item-head__origin" :title="eventItem.origin">{{ truncateText(eventItem.origin || 'Origem não informada', 60) }}</span>
                <span class="hd-item-head__values">
                  <span v-if="eventItem.amount">
                    {{ eventItem.currency | enum(dataset.currencyTypes, "id", "currencyFormat") }} {{ eventItem.amount | currency }}
                  </span>
                  <span v-else class="hd-muted">Valor não informado</span>
                  <span v-if="eventItem.partial_amount" class="hd-pill hd-pill--partial">
                    Aprovado {{ eventItem.currency | enum(dataset.currencyTypes, "id", "currencyFormat") }} {{ eventItem.partial_amount | currency }}
                  </span>
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <dl class="hd-facts">
                <div>
                  <dt>Devedora</dt>
                  <dd>{{ item.client.name }}</dd>
                </div>
                <div>
                  <dt>Moeda</dt>
                  <dd>{{ eventItem.currency | enum(dataset.currencyTypes, "id", "name") }}</dd>
                </div>
                <div>
                  <dt>Valor</dt>
                  <dd>{{ eventItem.currency | enum(dataset.currencyTypes, "id", "currencyFormat") }} {{ eventItem.amount | currency }}</dd>
                </div>
                <div v-if="eventItem.partial_amount">
                  <dt>Valor aprovado</dt>
                  <dd>{{ eventItem.currency | enum(dataset.currencyTypes, "id", "currencyFormat") }} {{ eventItem.partial_amount | currency }}</dd>
                </div>
                <div class="hd-facts__wide">
                  <dt>Origem</dt>
                  <dd>{{ eventItem.origin || "—" }}</dd>
                </div>
                <div v-if="eventItem.observation" class="hd-facts__wide">
                  <dt>Observações</dt>
                  <dd>{{ eventItem.observation }}</dd>
                </div>
              </dl>

              <div class="hd-subhead">Documentos <span>{{ eventItem.docs.length }} {{ eventItem.docs.length === 1 ? "arquivo" : "arquivos" }}</span></div>
              <div v-for="(itemDoc, docIndex) in eventItem.docs" :key="docIndex" class="hd-doc" role="button" tabindex="0"
                @click="openDoc(itemDoc.doc.file_url)" @keyup.enter="openDoc(itemDoc.doc.file_url)">
                <div class="hd-doc__icon"><v-icon small>mdi-file-document-outline</v-icon></div>
                <div class="hd-doc__info">
                  <div class="hd-doc__name">{{ itemDoc.doc.name }}</div>
                  <div class="hd-muted">{{ itemDoc.doc.type | enum(dataset.documentTypes, 'id', 'name') }}</div>
                </div>
                <v-icon small color="darkSecondary">mdi-open-in-new</v-icon>
              </div>

              <template v-if="item.status !== 0">
                <div class="hd-subhead mt-5">Motivo</div>
                <div class="hd-reason" v-html="completeReason ? item.reason : item.small_reason"></div>
                <v-btn text rounded small color="darkSecondary" class="hd-action mt-2" @click.native="completeReason = !completeReason">
                  {{ completeReason ? 'Ocultar motivo completo' : 'Ver motivo completo' }}
                  <v-icon right small>{{ completeReason ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </template>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </article>

      <!-- Modal para enviar os documentos solicitados-->
      <v-dialog v-model="dialogUpdateRequestedDocs" max-width="800px">
        <v-card>
          <v-card-title class="card-title">
            Atualização de documentos
          </v-card-title>
          <br>

          <v-card-text>
            <p>Para mais detalhes sobre a documentação ausente, entre em contato por telefone através dos números
              indicados neste site.</p>
          </v-card-text>
          <v-divider></v-divider>

          <v-progress-linear v-if="documentUploadLoading" indeterminate color="blue">
          </v-progress-linear>

          <v-card-text>
            <v-container v-if="mainDocs.length > 0">
              <v-row>
                <v-col cols="12">
                  <h3>Documentos Principais</h3>
                  <v-list dense>
                    <v-list-item v-for="doc in mainDocs" :key="doc.id" class="align-start" style="margin-bottom: 20px;">
                      <v-row class="d-flex align-center justify-space-between">
                        <v-col cols="8" class="d-flex align-center">
                          <v-list-item-content class="doc-title">
                            <v-list-item-title>- {{ doc.name }}</v-list-item-title>
                          </v-list-item-content>
                        </v-col>

                        <div class="d-flex align-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-if="!doc.uploaded" :loading="isLoading(doc)" :disabled="isLoading(doc)"
                                @click="doc.selectedFile ? uploadDocument(doc, 1) : openFileDialog(doc)" outlined
                                color="primary" v-bind="attrs" v-on="on">
                                {{ doc.selectedFile ? 'Enviar' : 'Atualizar' }}
                              </v-btn>
                            </template>
                            <span v-if="!doc.selectedFile">Selecionar documento para ser enviado</span>
                            <span v-if="doc.selectedFile">Enviar documento selecionado</span>
                          </v-tooltip>

                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-show="!doc.uploaded && doc.selectedFile && doc.uploadStatus !== 'error'"
                                class="ml-2" color="red" @click="clearSelectedFile(doc)" v-bind="attrs" v-on="on">
                                mdi-close
                              </v-icon>
                            </template>
                            <span>Limpar e selecionar outro documento</span>
                          </v-tooltip>

                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-show="doc.uploadStatus === 'error'" color="red" class="ml-3"
                                @click="retryUpload(doc)" v-bind="attrs" v-on="on">
                                mdi-close-circle
                              </v-icon>
                            </template>
                            <span>Erro no envio, clique aqui para tentar novamente</span>
                          </v-tooltip>
                        </div>

                        <div v-if="doc.uploaded || doc.uploadStatus === 'success'" class="d-flex align-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon v-show="!doc.selectedFile" color="green" class="ml-3" v-bind="attrs" v-on="on">
                                mdi-check-circle
                              </v-icon>
                            </template>
                            <span>Documento enviado</span>
                          </v-tooltip>

                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn outlined small color="primary" class="ml-2" :loading="isLoading(doc)"
                                :disabled="isLoading(doc)"
                                @click="doc.selectedFile ? uploadDocument(doc, 1) : openFileDialog(doc)" v-bind="attrs"
                                v-on="on">
                                <template v-slot:default>
                                  <span v-if="!(activeDocumentId === doc.id && documentUploadLoading)">
                                    {{ doc.selectedFile ? 'Enviar' : 'Alterar' }}
                                  </span>
                                </template>
                                <template v-slot:loader>
                                  <v-progress-circular indeterminate color="primary" size="25" width="3" />
                                </template>
                              </v-btn>
                            </template>
                            <template v-slot:default>
                              <span v-if="doc.selectedFile">Enviar documento selecionado</span>
                              <span v-if="!doc.selectedFile">Substituir documento enviado anteriormente</span>
                            </template>
                          </v-tooltip>
                        </div>

                      </v-row>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-container>

            <v-divider v-if="mainDocs.length > 0"></v-divider>

            <v-container v-if="businessDocs.length > 0">
              <v-row>
                <v-col cols="12">
                  <h3>Documentos Comprobatórios</h3>
                  <br>
                  <v-expansion-panels>
                    <v-expansion-panel v-for="(businessDoc, index) in businessDocs"
                      :key="businessDoc.origin + '-' + index">
                      <v-expansion-panel-header>
                        <div class="d-flex align-center">
                          <strong>Origem: </strong>
                          <span class="ml-2 mr-2">
                            {{ businessDoc.origin }}
                          </span>
                          /
                          <strong class="ml-2">Valor: </strong>
                          <span class="ml-2">
                            {{ businessDoc.amount }}
                          </span>
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-list dense>
                          <v-list-item v-for="(doc, docIndex) in businessDoc.docs" :key="doc.id + '-' + docIndex"
                            class="compact-item">

                            <v-list-item-content>
                              <v-list-item-title class="doc-title">
                                - {{ doc.name }}
                              </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action>

                              <div class="d-flex align-center">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-if="!doc.uploaded" :loading="isLoading(doc)" :disabled="isLoading(doc)"
                                      @click="doc.selectedFile ? uploadDocument(doc, 2) : openFileDialog(doc)" outlined
                                      color="primary" v-bind="attrs" v-on="on">
                                      {{ doc.selectedFile ? 'Enviar' : 'Atualizar' }}

                                    </v-btn>
                                  </template>
                                  <span v-if="!doc.selectedFile">Selecionar documento para ser enviado</span>
                                  <span v-if="doc.selectedFile">Enviar documento selecionado</span>
                                </v-tooltip>

                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-show="!doc.uploaded && doc.selectedFile && doc.uploadStatus !== 'error'"
                                      class="ml-2" color="red" @click="clearSelectedFile(doc)" v-bind="attrs" v-on="on">
                                      mdi-close
                                    </v-icon>
                                  </template>
                                  <span>Limpar e selecionar outro documento</span>
                                </v-tooltip>

                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-show="doc.uploadStatus === 'error'" color="red" class="ml-3"
                                      @click="retryUpload(doc)" v-bind="attrs" v-on="on">
                                      mdi-close-circle
                                    </v-icon>
                                  </template>
                                  <span>Erro no envio! Clique aqui para tentar novamente</span>
                                </v-tooltip>
                              </div>

                              <div v-if="doc.uploaded || doc.uploadStatus === 'success'" class="d-flex align-center">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-show="!doc.selectedFile" color="green" class="ml-3" v-bind="attrs"
                                      v-on="on">
                                      mdi-check-circle
                                    </v-icon>
                                  </template>
                                  <span>Documento enviado</span>
                                </v-tooltip>

                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn outlined small color="primary" class="ml-2" :loading="isLoading(doc)"
                                      :disabled="isLoading(doc)"
                                      @click="doc.selectedFile ? uploadDocument(doc, 2) : openFileDialog(doc)"
                                      v-bind="attrs" v-on="on">
                                      <span v-if="!isLoading(doc)">
                                        {{ doc.selectedFile ? 'Enviar' : 'Alterar' }}
                                      </span>
                                      <template v-slot:loader>
                                        <v-progress-circular indeterminate color="primary" size="25" width="3" />
                                      </template>
                                    </v-btn>
                                  </template>
                                  <template v-slot:default>
                                    <span v-if="doc.selectedFile">Enviar documento selecionado</span>
                                    <span v-if="!doc.selectedFile">Substituir documento enviado anteriormente</span>
                                  </template>
                                </v-tooltip>
                              </div>

                            </v-list-item-action>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="dialogUpdateRequestedDocs = false">Fechar</v-btn>
          </v-card-actions>
        </v-card>
        <input type="file" ref="fileInput" @change="handleFileChange" style="display: none" :accept="acceptedDocumentTypes"
          :key="selectedDocument && selectedDocument.selectedFile ? selectedDocument.selectedFile.name : 'new-file'" />
      </v-dialog>

      <v-dialog v-model="dialogViewDoc" max-width="60%" @keydown.esc="cancel">
        <v-card>
          <v-img :src="selectedDoc" alt="" contain />
        </v-card>
      </v-dialog>
    </div>

    <v-row>
      <whatsButton />
    </v-row>

    <v-row>
      <topButton />
    </v-row>
  </section>
</template>

<script>
import 'moment/locale/pt-br'

import currencyTypes from '@/common/data/currency-types.data'
import topButton from '@/components/layout/GoToTopButton.vue'
import whatsButton from '@/components/layout/WhatsAppButton.vue'
import ProcuratorCreditorSelector from '@/components/ProcuratorCreditorSelector.vue'

import creditorDocTypes from '@/common/data/creditor-email-doc-types.data'
import businessDocTypes from '@/common/data/business-doc-types.data'

import itemTypes from "@/common/data/item-types.data"
import moment from 'moment-timezone'

import axios from 'axios'
import { needsChunkedUpload, chunkedUpload, validateDocumentFile, ACCEPTED_DOCUMENT_TYPES } from '@/common/util/chunked-upload'

export default {
  components: {
    topButton,
    whatsButton,
    ProcuratorCreditorSelector
  },

  data: () => ({
    user: null,
    selectedCreditorId: null,
    data: [],
    loading: false,
    dataset: {
      documentTypes: itemTypes,
      contentUserAnonymous: "",
      contentUserLogged: "",
      currencyTypes: currencyTypes,
    },
    completeReason: false,
    selectedDoc: null,
    dialogViewDoc: false,
    dialogUpdateRequestedDocs: false,
    documentUploadLoading: false,
    // Progresso do envio em partes pela API (arquivos grandes / ZIP).
    documentUploadProgress: null,
    acceptedDocumentTypes: ACCEPTED_DOCUMENT_TYPES,
    selectedDocument: null,
    activeDocumentId: null,
    isLoaded: false,
    processedDocuments: [],
    businessDocs: [],
    mainDocs: [],
    processes: [],

    creditorDocTypes: creditorDocTypes,
    businessDocTypes: businessDocTypes,
    requestedDocOutOfDate: false,
    requestedDocDeadline: null,
    loadedRequestedEventId: null,
  }),

  mounted: function () { },

  computed: {
    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },
  },

  watch: {
    selectedCreditorId() {
      this.load();
    },
  },

  created() {

    this.$ga.event({
      eventCategory: 'page',
      eventAction: 'action',
      eventLabel: 'click',
      eventValue: 'user-area'
    })

    this.loadText();

    if (this.$route.query.token) {
      this.verifyAccount(this.$route.query.token)
    }

    if (this.$root.user) {
      this.loadUserInfo()
    }

  },

  methods: {
    truncateText(text, maxLength) {
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    },

    openDoc(doc) {
      window.open(doc, '_blank');
    },

    async loadText() {
      try {
        const result = await axios.get(`/api/dynamic-infos/keys`, {
          params: { keys: 'user-event-anonymous,user-event-logged' }
        })

        if (!result.data.content) return;

        let contentUserAnonymous = result.data.content.find((x) => x.key === 'user-event-anonymous');
        let contentUserLogged = result.data.content.find((x) => x.key === 'user-event-logged');

        this.dataset.contentUserAnonymous = contentUserAnonymous ? contentUserAnonymous.value : ""
        this.dataset.contentUserLogged = contentUserLogged ? contentUserLogged.value : ""

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 2000
        })
      }
    },

    async loadUserInfo() {
      this.loading = true

      try {
        const result = await axios.get('/api/users/info')

        this.user = result.data.content

        if (!this.isProcurator) this.load()
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    async load() {
      if (this.isProcurator && !this.selectedCreditorId) {
        this.data = [];
        return;
      }

      this.loading = true

      try {
        const result = await axios.get('/api/user-events/request', {
          params: {
            creditor_user_id: this.selectedCreditorId || undefined,
          },
        })

        this.data = result.data.content

        console.log("Data: ", this.data)
  
        this.processes = [];
  
        this.data.forEach(item => {
          if (Array.isArray(item.processes) && item.processes.length > 0) {
            this.processes = [...this.processes, ...item.processes]
          }
        })
  
        // Prazo de documentos solicitados por pedido (antes so o primeiro era considerado).
        this.data.forEach((item) => {
          const info = this.requestedDocInfo(item);
          this.$set(item, 'requested_deadline_label', info.label);
          this.$set(item, 'requested_out_of_date', info.outOfDate);
        });

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    isLoading(doc) {
      return this.activeDocumentId === doc.id && this.documentUploadLoading;
    },

    openFileDialog(doc) {
  
      const mainDocs = this.mainDocs || [];
      const businessDocs = this.businessDocs || [];
      const allDocs = [
        ...mainDocs,
        ...businessDocs.flatMap((item) => item.docs || []),
      ];

      const pendingUpload = allDocs.find((d) => d.selectedFile && !d.uploaded);

      if (pendingUpload) {
        this.$dialog.notify.error(
          `Você tem o documento "${pendingUpload.name}" pendente! Por favor, envie-o antes de selecionar outro.`,
          {
            position: "top-right",
            timeout: 5000,
          }
        );
        return;
      }

      this.selectedDocument = doc;
      this.$refs.fileInput.click();
    },
  
    handleFileChange(event) {
      const file = event.target.files[0];

      if (!this.selectedDocument || typeof this.selectedDocument !== "object") {
        this.$dialog.notify.error("Documento selecionado não é válido!", {
          position: "top-right",
          timeout: 5000,
        });
        return;
      }

      if (file) {
        this.$set(this.selectedDocument, "selectedFile", file);
      }
    },

    async uploadDocument(doc, type) {
      if (!doc.selectedFile) {
        this.$dialog.notify.error("Erro ao enviar o documento selecionado!", {
          position: "top-right",
          timeout: 5000,
        });
        return;
      }

      const fileError = validateDocumentFile(doc.selectedFile);
      if (fileError) {
        this.$dialog.notify.error(fileError, { position: "top-right", timeout: 5000 });
        return;
      }

      const formData = new FormData();
      formData.append("type", type);
      formData.append("ref", doc.id);

      if (doc.item_id) formData.append("item_id", doc.item_id);

      const targetList = type === 1 ? this.mainDocs : this.businessDocs;
      const targetDoc = this.findDocumentInList(targetList, doc.id, type);

      if (!targetDoc) {
        this.$dialog.notify.error("Documento não encontrado!", {
          position: "top-right",
          timeout: 5000,
        });
        return;
      }

      try {

        this.activeDocumentId = doc.id
        this.documentUploadLoading = true;

        // Arquivo grande ou ZIP vai antes, em partes; os demais seguem no multipart.
        if (needsChunkedUpload(doc.selectedFile)) {
          this.documentUploadProgress = 0;
          const uploaded = await chunkedUpload(doc.selectedFile, {
            onProgress: (percent) => { this.documentUploadProgress = percent; },
          });
          formData.append("uploaded_key", uploaded.key);
          formData.append("uploaded_name", uploaded.name);
        } else {
          formData.append("file", doc.selectedFile);
        }

        const response = await axios.patch(
          `/api/user-events/update-item-doc/${doc.event_id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },

          }
        );

        if (response.status === 200) {
          const docIndex = type === 1
            ? this.mainDocs.findIndex((d) => d.id === doc.id)
            : this.businessDocs.findIndex((businessDoc) =>
              businessDoc.item_id === doc.item_id &&
              businessDoc.docs.some((d) => d.id === doc.id)
            );

          if (type === 1 && docIndex !== -1) {
            this.$set(this.mainDocs, docIndex, {
              ...this.mainDocs[docIndex],
              uploadStatus: "success",
              uploaded: true,
              selectedFile: null,
            });
          } else if (type !== 1 && docIndex !== -1) {
            const businessDoc = this.businessDocs[docIndex];
            const innerDocIndex = businessDoc.docs.findIndex((d) => d.id === doc.id);

            if (innerDocIndex !== -1) {
              this.$set(businessDoc.docs, innerDocIndex, {
                ...businessDoc.docs[innerDocIndex],
                uploadStatus: "success",
                uploaded: true,
                selectedFile: null,
              });
            }
          }
          this.businessDocs.forEach((businessDoc) => {
            businessDoc.docs.forEach((doc) => {
              if (doc.selectedFile !== null) {
                doc.selectedFile = null;
              }
            });
          });

          this.$dialog.notify.success("Arquivo enviado com sucesso!", {
            position: "top-right",
            timeout: 5000,
          });
        } else {
          throw new Error("Erro não identificado!");
        }
      } catch (error) {

        const docIndex = type === 1
          ? this.mainDocs.findIndex((d) => d.id === doc.id)
          : this.businessDocs.findIndex((businessDoc) =>
            businessDoc.docs.some((d) => d.id === doc.id)
          );

        if (type === 1 && docIndex !== -1) {
          this.$set(this.mainDocs, docIndex, {
            ...this.mainDocs[docIndex],
            uploadStatus: "error",
            uploaded: false,
          });
        } else if (type !== 1 && docIndex !== -1) {
          const businessDoc = this.businessDocs[docIndex];
          const innerDocIndex = businessDoc.docs.findIndex((d) => d.id === doc.id);

          if (innerDocIndex !== -1) {
            this.$set(businessDoc.docs, innerDocIndex, {
              ...businessDoc.docs[innerDocIndex],
              uploadStatus: "error",
              uploaded: false,
            });
          }
        }

        this.$dialog.notify.error("Erro ao enviar o documento!", {
          position: "top-right",
          timeout: 5000,
        });
      }

      this.documentUploadLoading = false;
      this.documentUploadProgress = null;
      this.activeDocumentId = null;
    },

    findDocumentInList(list, docId, type) {
      if (type === 1) {
        return list.find((doc) => doc.id === docId);
      } else {
        for (const businessDoc of list) {
          const foundDoc = businessDoc.docs.find((d) => d.id === docId);
          if (foundDoc) {
            return foundDoc;
          }
        }
      }
      return null;
    },
  
    retryUpload(doc) {
      doc.uploadProgress = 0;
      doc.uploadStatus = "uploading";
      doc.selectedFile = null;
      this.openFileDialog(doc);
    },

    clearSelectedFile(doc) {
      doc.selectedFile = null;
    },

    // 72h uteis apos a solicitacao, como no aviso da lista.
    requestedDocInfo(item) {
      if (!item || !item.requested_doc_at) return { label: null, outOfDate: false };

      const deadlineDate = moment(item.requested_doc_at).utc().add(72, 'hours').utc();
      const dayOfWeek = deadlineDate.isoWeekday();
      if (dayOfWeek === 6) deadlineDate.add(2, 'days');
      else if (dayOfWeek === 7) deadlineDate.add(1, 'day');

      const now = moment().utc();
      return {
        label: `dia ${deadlineDate.tz('America/Sao_Paulo').format('DD/MM/YYYY [às] HH:mm')}`,
        outOfDate: now.isAfter(deadlineDate),
      };
    },

    loadRequestedDocuments(item) {

      if (!item) return;

      this.requestedDocDeadline = item.requested_deadline_label;
      this.requestedDocOutOfDate = item.requested_out_of_date;

      if (this.isLoaded && this.loadedRequestedEventId === item.id) {
        this.dialogUpdateRequestedDocs = true;
        return;
      }

      let requestedDocs = [];

      try {
        if (item.requested_doc) {
          requestedDocs = JSON.parse(item.requested_doc);
        }
      } catch (error) {
        this.$dialog.notify.error("Erro ao buscar a lista de documentos!", { position: 'top-right', timeout: 5000 });
        return;
      }

      const currencyType = item.items && item.items[0] && item.items[0].currency
        ? item.items[0].currency
        : 1;
      const currencySymbol = this.dataset && this.dataset.currencyTypes
        ? this.dataset.currencyTypes.find(type => type.id === currencyType).symbol || 'R$'
        : 'R$';

      if (Array.isArray(requestedDocs) && requestedDocs.length > 0) {
        const mainDocs = [];
        const businessDocsGrouped = [];

        requestedDocs.forEach((docGroup) => {
          if (docGroup.user_event_id) {
            // Documentos principais
            const mainDocumentNames = docGroup.docs.map((doc) => {
              const foundDoc = this.creditorDocTypes.find((ref) => ref.id === doc.id);
              return foundDoc
                ? {
                  ...foundDoc,
                  uploaded: doc.uploaded || false,
                  event_id: docGroup.user_event_id,
                  selectedFile: null,
                }
                : null;
            }).filter(doc => doc !== null && doc.id !== 3);

            mainDocs.push(...mainDocumentNames);
          } else {
            // Documentos de negócios
            const existingBusiness = businessDocsGrouped.find(
              (business) => business.item_id === docGroup.user_event_item_id
            );

            if (existingBusiness) {
              docGroup.docs.forEach((doc) => {
                const businessDoc = this.businessDocTypes.find((ref) => ref.id === doc.id);
                if (businessDoc) {
                  existingBusiness.docs.push({
                    ...businessDoc,
                    name: doc.id === 50 && doc.customName ? doc.customName : businessDoc.name,
                    uploaded: doc.uploaded || false,
                    event_id: item.id,
                    item_id: docGroup.user_event_item_id,
                    selectedFile: null,
                  });
                }
              });

            } else {
              const newBusinessDocs = docGroup.docs.map((doc) => {
                const businessDoc = this.businessDocTypes.find((ref) => ref.id === doc.id);
                return businessDoc
                  ? {
                    ...businessDoc,
                    name: doc.id === 50 && doc.customName ? doc.customName : businessDoc.name,
                    uploaded: doc.uploaded || false,
                    item_id: docGroup.user_event_item_id,
                    event_id: item.id,
                    uploadProgress: 0,
                    selectedFile: null,
                  }
                  : null;
              }).filter((doc) => doc !== null);

              const formattedAmount = new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: this.getCurrencyCode(currencyType),
              }).format(docGroup.amount || 0);

              businessDocsGrouped.push({
                item_id: docGroup.user_event_item_id,
                event_id: item.id,
                origin: docGroup.origin,
                amount: formattedAmount,
                docs: newBusinessDocs,
              });
            }
          }
        });

        this.isLoaded = true;
        this.loadedRequestedEventId = item.id;
        this.mainDocs = mainDocs;
        this.businessDocs = businessDocsGrouped;
        this.dialogUpdateRequestedDocs = true;

      }
    },

    async verifyAccount(token) {
      if (!token) return

      this.loading = true

      try {
        const normalizedToken = decodeURIComponent(token.toString().replaceAll(' ', '+'))

        const result = await axios.post('/api/users/verify-account', { token: normalizedToken })

        if (!result.data.status) throw new Error()

        await this.$dialog.confirm({
          text: 'Conta verificada com sucesso, realize o login para acessar a "Área do Credor"!',
          title: 'Sucesso',
          actions: {
            true: 'OK'
          }
        })

        this.$router.replace('/area-credor')
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
      } finally {
        this.loading = false
      }
    },

    getCurrencyCode(currencyType) {
      switch (currencyType) {
        case 1:
          return 'BRL';
        case 2:
          return 'USD';
        case 3:
          return 'EUR';
        default:
          return 'BRL';
      }
    },

    formatValue(value, currency) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      let options = {
        style: 'currency',
        currency: 'BRL'
      }

      switch (currency) {
        case 1:
          options.currency = 'BRL'
          break
        case 2:
          options.currency = 'USD'
          break
        case 3:
          options.currency = 'EUR'
          break
        default:
          options.currency = 'BRL'
      }

      return new Intl.NumberFormat('pt-BR', options).format(value)
    },


    // Decisoes (aprovado/recusado/parcial) so aparecem depois do relatorio publicado;
    // "documentos solicitados/enviados" nao e decisao e precisa aparecer sempre.
    displayStatus(item) {
      if (Number(item.status) === 4 || Number(item.status) === 5) return Number(item.status);
      return item.client && item.client.event_report_file ? item.status : 0;
    },

    getColorByStatus(status) {
      switch (status) {
        case 4:
          return '#FF6F00'
        case 5:
          return 'blue'
        case 0:
          return 'warning'
        case 1:
          return 'green'
        case 2:
          return 'important'
        case 3:
          return '#33691E'

        default:
          break
      }
    },

    // Tom da pilula do status (mesmas faixas das cores anteriores).
    statusTone(status) {
      return { 0: 'warn', 1: 'ok', 2: 'danger', 3: 'partial', 4: 'alert', 5: 'info' }[Number(status)] || 'info';
    },

    getTextByStatus(status) {
      switch (status) {
        case 4:
          return 'Documentos solicitados'
        case 5:
          return 'Documentos enviados'
        case 0:
          return 'Aguardando'
        case 1:
          return 'Aprovado'
        case 2:
          return 'Recusado'
        case 3:
          return 'Aprovado parcial'

        default:
          break
      }
    },

    editItem(item) {

      this.$router.push(`habilitacoes-divergencias/${item.id}`);
      window.scrollTo(0, 0);

    },

    viewCreateEvent() {
      const route = { path: 'habilitacoes-divergencias/novo' };
      if (this.selectedCreditorId) route.query = { creditor_user_id: this.selectedCreditorId };

      this.$router.push(route);
    }
  }
}
</script>

<style lang="scss">
.title-page {
  color: var(--v-primary-base);
  font-family: 'Raleway';
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: 38px;
}

.section-info-title {
  color: var(--v-primary-base);
  font-family: 'Raleway';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
}

.section-info-value {
  font-family: 'Raleway';
  font-size: 14px;
  font-style: normal;
}

.list-reset {
  padding-left: 0;
  padding-right: 0;
}

.alert-text-wrap {
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
}

.button-with-border {
  background-color: #ffffff;
  border: 2px solid #FF6F00;
  padding: 10px 20px;
  border-radius: 5px;
}
  
.button-with-border:hover {
  background-color: #f7994d;
  color: rgb(247, 243, 243) !important;
}

.card-title {
  border-bottom: 4px solid #2373c9 !important;
  padding-bottom: 8px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  // font-family: 'Raleway' !important;
  font-weight: 650 !important;
  font-size: 20px !important;
  font-style: bold !important;
  text-align: center !important;
}
</style>

<style lang="scss" scoped>
/* Mesmo padrao visual de "Meus credores", "Minha conta" e "Resumo de casos". */

.hd-header {
  padding: 32px 0 24px;
}

.hd-eyebrow {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #766386;
}

.hd-title {
  margin: 14px 0 10px;
  font-size: clamp(28px, 3.4vw, 40px);
  font-weight: 700;
  color: #25113c;
  letter-spacing: -0.035em;
  line-height: 1.2;
}

.hd-title span {
  color: #806697;
}

.hd-subtitle {
  margin: 0;
  color: #73677c;
  font-size: 14px;
}

.hd-muted {
  font-size: 13px;
  color: rgba(24, 0, 50, 0.55);
}

.hd-intro {
  padding: 16px 20px;
  border-radius: 14px;
  background: rgba(120, 119, 229, 0.09);
  color: #2a1a40;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 24px;
}

.hd-intro ::v-deep p:last-child {
  margin-bottom: 0;
}

/* Seletor do credor representado com contorno lilas */
.hd-field {
  max-width: 520px;
}

.hd-field ::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(126, 85, 171, 0.45);
  border-radius: 10px;
}

.hd-field ::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #7e55ab;
}

.hd-field ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

.hd-field ::v-deep .v-alert {
  border-radius: 12px;
}

.hd-list-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 8px 0 12px;
}

.hd-list-head h2 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7e55ab;
}

.hd-list-head span {
  font-size: 12px;
  color: rgba(24, 0, 50, 0.5);
}

/* Cartao do pedido */
.hd-card {
  padding: 18px 20px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(24, 0, 50, 0.07);
  margin-bottom: 14px;
}

.hd-card__head {
  display: flex;
  align-items: center;
  gap: 12px 16px;
  flex-wrap: wrap;
}

.hd-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
  flex-shrink: 0;
}

.hd-card__main {
  flex: 1 1 260px;
  min-width: 0;
}

.hd-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #180032;
}

/* Botoes */
.hd-btn {
  height: 38px !important;
  padding: 0 16px !important;
  border-radius: 10px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.hd-action {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

/* Pilulas de status */
.hd-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.hd-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.hd-pill--ok {
  background: #e7f6ee;
  color: #1e7b4f;
}

.hd-pill--partial {
  background: #e3f1fc;
  color: #1d5f99;
}

.hd-pill--info {
  background: rgba(120, 119, 229, 0.14);
  color: #4b3a8f;
}

.hd-pill--warn {
  background: #fff4dc;
  color: #a16207;
}

.hd-pill--alert {
  background: #ffedd5;
  color: #c2410c;
}

.hd-pill--danger {
  background: #fdecea;
  color: #b42318;
}

/* Aviso de documentos solicitados */
.hd-notice {
  display: flex;
  align-items: center;
  gap: 12px 14px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-radius: 12px;
  margin-top: 14px;
  font-size: 14px;
  line-height: 1.55;
  color: #2a1a40;
}

.hd-notice--warn {
  background: #fff7e6;
}

.hd-notice--muted {
  background: #f1f0f4;
}

.hd-notice__text {
  flex: 1 1 320px;
}

/* Itens do pedido */
.hd-items {
  margin-top: 14px;
}

.hd-items ::v-deep .v-expansion-panel {
  border: 1px solid rgba(24, 0, 50, 0.07);
  border-radius: 12px !important;
  background: #faf9fd;
  margin-bottom: 8px;
}

.hd-items ::v-deep .v-expansion-panel::before {
  box-shadow: none;
}

.hd-items ::v-deep .v-expansion-panel::after {
  border-top: 0 !important;
}

.hd-items ::v-deep .v-expansion-panel-header {
  min-height: 52px;
  padding: 12px 16px;
}

.hd-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  flex-wrap: wrap;
  width: 100%;
  padding-right: 8px;
}

.hd-item-head__origin {
  font-weight: 600;
  color: #180032;
  min-width: 0;
}

.hd-item-head__values {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-weight: 600;
  color: #25113c;
}

.hd-facts {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px 20px;
  margin: 4px 0 18px;
}

.hd-facts__wide {
  grid-column: 1 / -1;
}

.hd-facts dt {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8b769d;
}

.hd-facts dd {
  margin: 2px 0 0;
  font-size: 14px;
  color: #25113c;
  overflow-wrap: anywhere;
}

.hd-subhead {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7e55ab;
  margin-bottom: 6px;
}

.hd-subhead span {
  margin-left: 6px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(24, 0, 50, 0.5);
}

.hd-doc {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(24, 0, 50, 0.06);
  margin-bottom: 6px;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease;
}

.hd-doc:hover,
.hd-doc:focus-visible {
  border-color: rgba(120, 119, 229, 0.45);
}

.hd-doc__icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
  color: #7e55ab;
}

.hd-doc__icon .v-icon {
  color: inherit;
}

.hd-doc__name {
  font-size: 14px;
  font-weight: 600;
  color: #180032;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hd-reason {
  font-size: 14px;
  line-height: 1.6;
  color: #2a1a40;
}

/* Estado vazio */
.hd-empty {
  text-align: center;
  padding: 40px 16px 56px;
}

.hd-empty__icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(120, 119, 229, 0.12);
}

.hd-empty__title {
  font-size: 17px;
  font-weight: 600;
  color: #180032;
}

.hd-empty__text {
  margin-top: 6px;
  color: rgba(24, 0, 50, 0.6);
  font-size: 14px;
}

/* Celular */
@media (max-width: 760px) {
  .hd-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
